import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BulkSchedulingPanel = ({ 
  selectedEmployees, 
  onBulkUpdate, 
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [bulkData, setBulkData] = useState({
    status: 'available',
    startTime: '۰۹:۰۰',
    endTime: '۱۷:۰۰',
    selectedDays: [],
    dateRange: {
      start: '',
      end: ''
    },
    breakTime: {
      enabled: false,
      start: '۱۲:۰۰',
      end: '۱۳:۰۰'
    }
  });

  const statusOptions = [
    { value: 'available', label: 'در دسترس' },
    { value: 'unavailable', label: 'غیرفعال' }
  ];

  const timeOptions = [
    { value: '۰۸:۰۰', label: '۰۸:۰۰' },
    { value: '۰۸:۳۰', label: '۰۸:۳۰' },
    { value: '۰۹:۰۰', label: '۰۹:۰۰' },
    { value: '۰۹:۳۰', label: '۰۹:۳۰' },
    { value: '۱۰:۰۰', label: '۱۰:۰۰' },
    { value: '۱۰:۳۰', label: '۱۰:۳۰' },
    { value: '۱۱:۰۰', label: '۱۱:۰۰' },
    { value: '۱۱:۳۰', label: '۱۱:۳۰' },
    { value: '۱۲:۰۰', label: '۱۲:۰۰' },
    { value: '۱۲:۳۰', label: '۱۲:۳۰' },
    { value: '۱۳:۰۰', label: '۱۳:۰۰' },
    { value: '۱۳:۳۰', label: '۱۳:۳۰' },
    { value: '۱۴:۰۰', label: '۱۴:۰۰' },
    { value: '۱۴:۳۰', label: '۱۴:۳۰' },
    { value: '۱۵:۰۰', label: '۱۵:۰۰' },
    { value: '۱۵:۳۰', label: '۱۵:۳۰' },
    { value: '۱۶:۰۰', label: '۱۶:۰۰' },
    { value: '۱۶:۳۰', label: '۱۶:۳۰' },
    { value: '۱۷:۰۰', label: '۱۷:۰۰' },
    { value: '۱۷:۳۰', label: '۱۷:۳۰' },
    { value: '۱۸:۰۰', label: '۱۸:۰۰' },
    { value: '۱۸:۳۰', label: '۱۸:۳۰' },
    { value: '۱۹:۰۰', label: '۱۹:۰۰' },
    { value: '۱۹:۳۰', label: '۱۹:۳۰' }
  ];

  const persianDays = [
    { value: 'saturday', label: 'شنبه' },
    { value: 'sunday', label: 'یکشنبه' },
    { value: 'monday', label: 'دوشنبه' },
    { value: 'tuesday', label: 'سه‌شنبه' },
    { value: 'wednesday', label: 'چهارشنبه' },
    { value: 'thursday', label: 'پنج‌شنبه' },
    { value: 'friday', label: 'جمعه' }
  ];

  const handleInputChange = (field, value) => {
    setBulkData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setBulkData(prev => ({
      ...prev,
      [parent]: {
        ...prev?.[parent],
        [field]: value
      }
    }));
  };

  const handleDayToggle = (day) => {
    setBulkData(prev => ({
      ...prev,
      selectedDays: prev?.selectedDays?.includes(day)
        ? prev?.selectedDays?.filter(d => d !== day)
        : [...prev?.selectedDays, day]
    }));
  };

  const handleSelectAllDays = () => {
    setBulkData(prev => ({
      ...prev,
      selectedDays: persianDays?.map(day => day?.value)
    }));
  };

  const handleClearAllDays = () => {
    setBulkData(prev => ({
      ...prev,
      selectedDays: []
    }));
  };

  const handleApplyBulkUpdate = () => {
    if (selectedEmployees?.length === 0) {
      alert('لطفاً ابتدا کارمندان مورد نظر را انتخاب کنید');
      return;
    }

    if (bulkData?.selectedDays?.length === 0) {
      alert('لطفاً حداقل یک روز را انتخاب کنید');
      return;
    }

    onBulkUpdate?.(bulkData);
    
    // Reset form
    setBulkData(prev => ({
      ...prev,
      selectedDays: [],
      dateRange: { start: '', end: '' }
    }));
    
    setIsExpanded(false);
  };

  const getQuickSchedulePresets = () => [
    {
      label: 'ساعات کاری عادی',
      data: {
        status: 'available',
        startTime: '۰۹:۰۰',
        endTime: '۱۷:۰۰',
        selectedDays: ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
        breakTime: { enabled: true, start: '۱۲:۰۰', end: '۱۳:۰۰' }
      }
    },
    {
      label: 'نیمه وقت صبح',
      data: {
        status: 'available',
        startTime: '۰۸:۰۰',
        endTime: '۱۲:۰۰',
        selectedDays: ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday'],
        breakTime: { enabled: false, start: '۱۲:۰۰', end: '۱۳:۰۰' }
      }
    },
    {
      label: 'نیمه وقت عصر',
      data: {
        status: 'available',
        startTime: '۱۴:۰۰',
        endTime: '۱۸:۰۰',
        selectedDays: ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday'],
        breakTime: { enabled: false, start: '۱۲:۰۰', end: '۱۳:۰۰' }
      }
    }
  ];

  const applyPreset = (preset) => {
    setBulkData(prev => ({
      ...prev,
      ...preset?.data
    }));
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-right hover:bg-muted transition-colors duration-150 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <Icon name="Settings" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">تنظیمات گروهی</h3>
            <p className="text-sm text-muted-foreground">
              اعمال تغییرات به {selectedEmployees?.length} کارمند انتخاب شده
            </p>
          </div>
        </div>
        <Icon 
          name="ChevronDown" 
          size={20} 
          className={`text-muted-foreground transition-transform duration-150 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isExpanded && (
        <div className="border-t border-border p-4 space-y-4 animate-fade-in">
          {/* Quick Presets */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              الگوهای آماده
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {getQuickSchedulePresets()?.map((preset, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(preset)}
                  className="text-xs"
                >
                  {preset?.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Status and Time Range */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Select
              label="وضعیت"
              options={statusOptions}
              value={bulkData?.status}
              onChange={(value) => handleInputChange('status', value)}
            />
            <Select
              label="زمان شروع"
              options={timeOptions}
              value={bulkData?.startTime}
              onChange={(value) => handleInputChange('startTime', value)}
            />
            <Select
              label="زمان پایان"
              options={timeOptions}
              value={bulkData?.endTime}
              onChange={(value) => handleInputChange('endTime', value)}
            />
          </div>

          {/* Days Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">
                انتخاب روزها
              </label>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleSelectAllDays}
                >
                  همه
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleClearAllDays}
                >
                  هیچکدام
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {persianDays?.map((day) => (
                <button
                  key={day?.value}
                  onClick={() => handleDayToggle(day?.value)}
                  className={`
                    p-2 text-sm rounded border transition-colors duration-150
                    ${bulkData?.selectedDays?.includes(day?.value)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-foreground border-border hover:bg-muted'
                    }
                  `}
                >
                  {day?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="از تاریخ (اختیاری)"
              type="date"
              value={bulkData?.dateRange?.start}
              onChange={(e) => handleNestedInputChange('dateRange', 'start', e?.target?.value)}
            />
            <Input
              label="تا تاریخ (اختیاری)"
              type="date"
              value={bulkData?.dateRange?.end}
              onChange={(e) => handleNestedInputChange('dateRange', 'end', e?.target?.value)}
            />
          </div>

          {/* Break Time */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="breakTime"
                checked={bulkData?.breakTime?.enabled}
                onChange={(e) => handleNestedInputChange('breakTime', 'enabled', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="breakTime" className="text-sm font-medium text-foreground">
                زمان استراحت
              </label>
            </div>

            {bulkData?.breakTime?.enabled && (
              <div className="grid grid-cols-2 gap-3 bg-muted rounded-lg p-3">
                <Select
                  label="شروع استراحت"
                  options={timeOptions}
                  value={bulkData?.breakTime?.start}
                  onChange={(value) => handleNestedInputChange('breakTime', 'start', value)}
                />
                <Select
                  label="پایان استراحت"
                  options={timeOptions}
                  value={bulkData?.breakTime?.end}
                  onChange={(value) => handleNestedInputChange('breakTime', 'end', value)}
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setIsExpanded(false)}
            >
              انصراف
            </Button>
            <Button
              variant="default"
              onClick={handleApplyBulkUpdate}
              iconName="Save"
              iconPosition="right"
              disabled={selectedEmployees?.length === 0 || bulkData?.selectedDays?.length === 0}
            >
              اعمال تغییرات
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkSchedulingPanel;